import React from "react";
import { Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import Datetime from "react-datetime";
import moment from "moment";
import { CalendarIcon } from "../Icons";
// import "react-datetime/css/react-datetime.css";
import { getIn } from "formik";

const getFormikError = (formik, field) => {
  return getIn(formik.touched, field) && getIn(formik.errors, field);
};

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  dateFormat = "MMM DD, YYYY",
  name = "dateField",
  timeFormat = false,
  closeOnSelect = true,
  closeOnClickOutside = true,
  readOnly = true,
  isValidDate,
  disabled = false,
  renderDay,
  isInvalid = false,
}) {
  const parsedValue =
    value &&
    moment(value, ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"], true).isValid()
      ? moment(value, ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"], true).toDate()
      : value || "";

  return (
    <div className="position-relative ">
      <Datetime
        timeFormat={timeFormat}
        closeOnSelect={closeOnSelect}
        closeOnClickOutside={closeOnClickOutside}
        dateFormat={dateFormat}
        name={name}
        value={parsedValue}
        onChange={onChange}
        inputProps={{
          placeholder: placeholder,
          readOnly: readOnly,
          style: {
            borderRadius: "5px",
            height: "2.75rem",
            padding: "0.5rem 0.75rem",
            fontSize: "0.875rem", // G.A. Standard font size
            border: isInvalid ? "1px solid #dc3545" : "1px solid #ced4da",
          },
          disabled,
          className: isInvalid
            ? "date-input form-control is-invalid"
            : "date-input form-control",
        }}
        isValidDate={isValidDate}
        renderDay={renderDay}
      />
      <CalendarIcon
        className="position-absolute"
        style={{
          right: "12px",
          stroke: "1px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          color: "#6c757d",
          width: "18px",
          height: "18px",
        }}
      />
    </div>
  );
}

export default function MonthYearPicker({
  // Formik mode
  formik,
  monthFieldName = "month",
  yearFieldName = "year",

  // Custom onChange mode
  monthValue,
  yearValue,
  onMonthChange,
  onYearChange,
  onMonthYearChange, // Combined callback for both values

  // Labels and options
  monthLabel = "Month",
  yearLabel = "Year",
  showRequired = true,
  monthOptions: customMonthOptions = monthOptions,
  colSize = 6,
  monthError,
  yearError,

  // Styling variant
  variant = "form", // "form" or "filter"
  monthPlaceholder = "Select Month",
  yearPlaceholder = "Select Year",
}) {
  // Determine if using formik or custom callbacks
  const useFormik = !!formik;

  // Get month value based on mode
  const getMonthValue = () => {
    return useFormik ? formik.values[monthFieldName] || "" : monthValue || "";
  };

  // Get year value based on mode
  const getYearValue = () => {
    return useFormik
      ? formik.values[yearFieldName]
        ? new Date(formik.values[yearFieldName], 0, 1)
        : null
      : yearValue
      ? new Date(yearValue, 0, 1)
      : null;
  };

  // Handle month change based on mode
  const handleMonthChange = selected => {
    const monthVal = selected ? selected.value : "";
    const yearVal = useFormik ? formik.values[yearFieldName] : yearValue;

    if (useFormik) {
      formik.setFieldValue(monthFieldName, monthVal);
    } else {
      if (onMonthChange) {
        onMonthChange(monthVal);
      }
      if (onMonthYearChange) {
        onMonthYearChange({ month: monthVal, year: yearVal });
      }
    }
  };

  // Handle year change based on mode
  const handleYearChange = date => {
    const selectedYear = date
      ? new Date(date).getFullYear()
      : new Date().getFullYear();
    const monthVal = useFormik ? formik.values[monthFieldName] : monthValue;

    if (useFormik) {
      formik.setFieldValue(yearFieldName, selectedYear);
    } else {
      if (onYearChange) {
        onYearChange(selectedYear);
      }
      if (onMonthYearChange) {
        onMonthYearChange({ month: monthVal, year: selectedYear });
      }
    }
  };

  // Get error message based on mode
  const getMonthError = () => {
    if (useFormik) {
      return getFormikError(formik, monthFieldName);
    }
    return monthError;
  };

  const getYearError = () => {
    return yearError;
  };

  // Month field content
  const monthFieldContent = (
    <>
      {monthLabel && (
        <Form.Label>
          {monthLabel}
          {showRequired && variant !== "filter" && (
            <span className="text-danger">*</span>
          )}
        </Form.Label>
      )}
      <Select
        name={monthFieldName}
        options={customMonthOptions}
        value={
          customMonthOptions.find(opt => opt.value === getMonthValue()) || null
        }
        onChange={handleMonthChange}
        onBlur={useFormik ? formik.handleBlur : undefined}
        placeholder={monthPlaceholder}
        isClearable
        classNamePrefix="form-select"
        menuPlacement="bottom"
        menuPosition="fixed"
      />
      {getMonthError() && variant === "form" && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {getMonthError()}
        </Form.Control.Feedback>
      )}
    </>
  );

  // Year field content
  const yearFieldContent = (
    <>
      {yearLabel && (
        <Form.Label>
          {yearLabel}
          {showRequired && variant !== "filter" && (
            <span className="text-danger">*</span>
          )}
        </Form.Label>
      )}
      <DatePicker
        name={yearFieldName}
        value={getYearValue()}
        onChange={handleYearChange}
        placeholder={yearPlaceholder}
        dateFormat="YYYY"
        timeFormat={false}
      />
      {getYearError() && variant === "form" && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {getYearError()}
        </Form.Control.Feedback>
      )}
    </>
  );

  // Filter variant: simple flex layout
  if (variant === "filter") {
    return (
      <div className="d-flex gap-2">
        <Form.Group>{monthFieldContent}</Form.Group>
        <Form.Group>{yearFieldContent}</Form.Group>
      </div>
    );
  }

  // Form variant: Row/Col layout
  return (
    <Row>
      <Col md={colSize}>
        <Form.Group className="mb-3">{monthFieldContent}</Form.Group>
      </Col>

      <Col md={colSize}>
        <Form.Group className="mb-3">{yearFieldContent}</Form.Group>
      </Col>
    </Row>
  );
}
